<template>
    <div class="loginContainer">
      <dean-header :logo="headerLogo" logo-height="35"></dean-header>

      <div class="dialogContainer">
        <alert-notification/>
        <dean-dialog
                class="login-dialog"
                title="Wachtwoord vergeten"
                :icon="lockIcon"
                align-top>
          <alert-notification></alert-notification>

          <span class="dean-dialog-logo" slot="dean-dialog-logo">
            <dean-icon class="deanone-logo" :glyph="headerLogo"></dean-icon>
          </span>

          <span class="fields">
            <dean-input-field
                    @enter="forgotPasswordClick"
                    id="email"
                    name="email"
                    v-model="data.email"
                    required-field="true"
                    type="text"
                    :warning="$v.data.email.$error"
                    @focus="$v.data.email.$touch()">
              {{ this.$t('inputs.email') }}
            </dean-input-field>
          </span>

          <span id="actions">
            <span class="auth-forgot-password" @click="goToLogin">Terug</span>
            <dean-button class="auth-login" primary="true" button-primary dialog-button @dean-button-click="forgotPasswordClick">Verzenden</dean-button>
          </span>
        </dean-dialog>
      </div>
    </div>
</template>

<script lang="ts">
  // VUE
  import {
    Vue,
    Component,
  } from 'vue-property-decorator';

  import {
    deanButton,
    deanHeader,
    lockIcon,
    warningIcon,
    deanIcon,
  } from '@deanone/dean-components';

  import {required, email} from 'vuelidate/lib/validators';
  import {Validations} from 'vuelidate-property-decorators';

  import alertNotification from '@/components/deanComponents/deanNotification.vue';
  import deanDialog from '@/components/deanComponents/deanDialog.vue';
  import deanInputField from '@/components/deanComponents/deanInputField.vue';
  import {createNotification} from '@/mixins/validations';
  import {getErrorCodeFromResponse, getErrorMessageFromResponse, getErrorMessageFromTranslationByErrorCode} from '@/mixins/errorMessage';
  import {redirectToKeycloakResetCredentials} from '@/services/keycloak';

  @Component({
    components: {
      deanInputField,
      deanButton,
      deanDialog,
      deanHeader,
      deanIcon,
      alertNotification,
    },
  })

  /**
   * Forgot password page (Keycloak migration flow).
   * Only relevant for users that are not yet migrated to Keycloak. Submitting this form
   * migrates the user into Keycloak without a password. The browser is then redirected
   * straight to Keycloak's own "Forgot password" page (with the email prefilled), where
   * Keycloak itself handles emailing the user a link to set their password — the WS
   * Portal never sends this email itself.
   */
  export default class ForgotPassword extends Vue {
    public lockIcon: object = lockIcon;
    public warningIcon: object = warningIcon;
    public data: {email: string | null} = {
      email: null,
    };

    @Validations()
    validations = {
      data: {
        email: {required, email},
      },
    };

    get headerLogo() {
      return this.$store.getters.getTheme.data.loginLogo;
    }

    public created() {
      const {email} = this.$route.query;
      this.data.email = typeof email === 'string' ? email : null;
    }

    public forgotPasswordClick() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$toast(createNotification(this.$v));
        return;
      }

      this.$store.dispatch('setLoadOverlay', true);
      this.$store.dispatch('forgotPasswordUser', this.data.email).then(
        () => {
          this.$store.dispatch('setLoadOverlay', false);
          redirectToKeycloakResetCredentials(this.data.email as string);
        },
        (error) => {
          this.$store.dispatch('setLoadOverlay', false);

          const errorResponse = error?.response?.data;
          const errorCode: string | null = errorResponse ? getErrorCodeFromResponse(errorResponse) : null;
          let errorMessage: string | null = null;

          if (errorCode) {
            errorMessage = getErrorMessageFromTranslationByErrorCode(errorCode);
          }

          if (errorMessage === null && errorResponse) {
            errorMessage = getErrorMessageFromResponse(errorResponse, null);
          }

          this.$toast({
            icon: this.warningIcon,
            title: 'Fout',
            text: errorMessage || (this.$t('messages.forgotPasswordError') as string),
            theme: 'danger',
          });
        },
      );
    }

    public goToLogin() {
      if (this.$route.name !== 'auth.login') {
        this.$router.push({name: 'auth.login', query: {email: this.data.email || undefined}}).catch(() => { /* ignore */ });
      }
    }
  }
</script>

<style lang="scss" scoped>
  // IMPORT
  @import '~@deanone/dean-components/src/scss/vars';

  .login-dialog {
    background: var(--color-light-shade-1, $color-light-shade-1);
  }

  .dean-dialog-button-group > span {
    display: flex;
    justify-content: flex-end;
    padding: 5px;
  }

  .login-dialog {
    .dean-dialog-logo {
      display: block;
      text-align: center;
      margin-bottom: 15px;
    }
  }

  .deanone-logo {
    width: 80px;
  }

  .fields {
    display: block;
    padding: 10px 0 15px;
  }

  #actions {
    padding:0 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;

    .auth-forgot-password {
      text-decoration: underline;
    }
  }
</style>
